import { type QueryClient, useQuery } from '@tanstack/react-query';
import type { PatientRecap, PatientRecapError } from '../../contracts';
import { GET_PATIENT_RECAP } from '../../contracts';
import { getPatientRecap } from '../api/patients';

/**
 * Invalidate the cached recap for a profile. The recap (linked-patient popover
 * + profile drawer) caches with `staleTime: Infinity`, so callers that change
 * its source (note finalise, link, unlink) must invalidate it explicitly or
 * both surfaces show stale data until a full reload. No-ops without an id.
 *
 * `'none'` still marks the query invalid, so the next mount refetches — pass it
 * when the caller is about to unmount the surface, so a refetch doesn't resolve
 * onto something that has already gone.
 *
 * Owns the query-key shape so call sites don't hand-roll it; a future key
 * change only has to update here.
 */
export function invalidatePatientRecap(
  queryClient: QueryClient,
  patientProfileId: string | null | undefined,
  refetchType: 'active' | 'inactive' | 'all' | 'none' = 'active'
) {
  if (!patientProfileId) return;
  void queryClient.invalidateQueries({
    queryKey: [GET_PATIENT_RECAP, { patientProfileId }],
    refetchType,
  });
}

export type PatientRecapQueryResult = {
  recap: PatientRecap | null;
  error: PatientRecapError | null;
};

export function useQueryPatientRecap(
  patientProfileId: string | null | undefined,
  /**
   * The app's `state.auth.token` read, composed app-side and passed in
   * (cohort-C). A package hook cannot reach the Rematch store, and injecting
   * the store would relocate the coupling rather than remove it.
   */
  isAuthenticated = true
) {
  return useQuery<PatientRecapQueryResult>({
    queryKey: [GET_PATIENT_RECAP, { patientProfileId }],
    enabled: isAuthenticated && !!patientProfileId,
    // Refreshes are driven by `invalidatePatientRecap`, not by staleness. The
    // backend caches the blurb on the session behind a hash of the note text,
    // model and prompt version, so a repeat read is a DB hit rather than a
    // generation — but it's still a round-trip for something that only moves
    // when that hash does.
    staleTime: Infinity,
    queryFn: async () => {
      if (!patientProfileId) return { recap: null, error: null };
      const { data } = await getPatientRecap(patientProfileId);
      return {
        recap: data.data ?? null,
        error: data.error ?? null,
      };
    },
  });
}
